import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
    booleanColumnBuilder,
    customColumnBuilder,
    dateColumnBuilder,
    datetimeColumnBuilder,
    multiSelectColumnBuilder,
    numericColumnBuilder,
    stringColumnBuilder,
} from './columnBuilder';
import _ from 'lodash';
import { DEFAULT_TIME_ZONE, getZoneOffsetMs } from '../../timeZones';

export const convertStringDateToJsDates = (table, columns, timeZone = DEFAULT_TIME_ZONE) => {
    const dateColumns = columns.filter((x) => x.dataType === 'date').map((x) => x.field);
    const datetimeColumns = columns
        .filter((x) => x.dataType === 'datetime')
        .map((x) => x.field);

    // PrimeReact insists on rendering and filtering date fields as browser-local time,
    // so we bake the intended wall-clock time into the Date itself. Everything downstream
    // (display, sorting, filtering, CSV export) then reads correctly.
    const parseAsUtcWallClock = (dateTimeString) => {
        const utcDate = new Date(dateTimeString);
        const offsetMinutes = utcDate.getTimezoneOffset();
        // getTimezoneOffset() returns positive values for timezones behind UTC
        // so we add the offset to counteract the local time conversion
        return new Date(utcDate.getTime() + offsetMinutes * 60 * 1000);
    };

    // Date-only values carry no time component to reinterpret, so they are never shifted by the selected report time zone
    const parseDateTime = (dateTimeString) => {
        if (timeZone === DEFAULT_TIME_ZONE) {
            return parseAsUtcWallClock(dateTimeString);
        }

        const utcDate = new Date(dateTimeString);
        return new Date(
            parseAsUtcWallClock(dateTimeString).getTime() +
                getZoneOffsetMs(utcDate, timeZone)
        );
    };

    table.forEach((row) => {
        dateColumns.forEach((column) => {
            row[column] = row[column] ? parseAsUtcWallClock(row[column]) : row[column];
        });
        datetimeColumns.forEach((column) => {
            row[column] = row[column] ? parseDateTime(row[column]) : row[column];
        });
    });
};

export const convertBooleansToStringMultiselect = (table, columns) => {
    const booleanColumns = columns.filter((x) => x.dataType === 'boolean');

    table.forEach((row) => {
        booleanColumns.forEach((column) => {
            row[column.field] = row[column.field] ? 'Yes' : 'No';
        });
    });

    booleanColumns.forEach((x) => (x.dataType = 'multiselect'));
};

export const decorateMultiselectColumnsWithOptions = (table, columns) => {
    const multiSelectColumns = columns.filter((x) => x.dataType === 'multiselect');

    multiSelectColumns.forEach((column) => {
        column.multiselectOptions = _(table)
            .map((y) => y[column.field])
            .filter(Boolean)
            .uniq()
            .sort()
            .value();
    });
};

export const getFilterDefaultByDataType = (dataType) => {
    switch (dataType) {
        case 'string':
            return {
                operator: FilterOperator.AND,
                constraints: [{ value: '', matchMode: FilterMatchMode.CONTAINS }],
            };
        case 'date':
            return {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_AFTER }],
            };
        case 'datetime':
            return {
                operator: FilterOperator.AND,
                constraints: [{ value: null, matchMode: FilterMatchMode.DATE_AFTER }],
            };
        case 'multiselect':
            return {
                operator: FilterOperator.AND,
                constraints: [{ value: [], matchMode: FilterMatchMode.IN }],
            };
        case 'numeric':
            return {
                operator: FilterOperator.AND,
                constraints: [
                    { value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
                ],
            };
        case 'boolean':
            return {
                operator: FilterOperator.AND,
                constraints: [{ value: '', matchMode: FilterMatchMode.EQUALS }],
            };
        case 'custom':
            // Custom columns don't have filters
            return null;
        default:
            throw new Error(
                'Unknown dataType provided to getFilterDefaultByDataType: ' + dataType
            );
    }
};

export const buildColumnByDataType = (column) => {
    switch (column.dataType) {
        case 'string':
            return stringColumnBuilder(
                column.field,
                column.name,
                column.styles,
                column.hidden
            );
        case 'date':
            return dateColumnBuilder(
                column.field,
                column.name,
                column.styles,
                column.hidden
            );
        case 'datetime':
            return datetimeColumnBuilder(
                column.field,
                column.name,
                column.styles,
                column.hidden
            );
        case 'multiselect':
            return multiSelectColumnBuilder(
                column.field,
                column.name,
                column.multiselectOptions,
                column.styles,
                column.hidden
            );
        case 'numeric':
            return numericColumnBuilder(
                column.field,
                column.name,
                column.styles,
                column.hidden
            );
        case 'boolean':
            // Note this is NOT used by the <StandardizedDataTable />
            return booleanColumnBuilder(
                column.field,
                column.name,
                column.styles,
                column.hidden
            );
        case 'custom':
            return customColumnBuilder(
                column.field,
                column.name,
                column.body,
                column.styles,
                column.hidden
            );
        default:
            throw new Error(
                'Unknown dataType provided to buildColumnByDataType: ' + column?.dataType
            );
    }
};
